import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Lock, EyeOff, ShieldOff, Fingerprint } from "lucide-react";
import { PageHead, Wrap, P, Prose, Impact, DontList } from "@/components/Content";
import { Seo, breadcrumbSchema } from "@/components/Seo";

const PERMISSION_OPTIONS = [
  { t: "NO — KEEP PRIVATE", d: "The default for every submission. Nothing about your card or collection is used publicly.", def: true },
  { t: "YES — WEBSITE", d: "You allow permitted before/after imagery to appear on this website only." },
  { t: "YES — SOCIAL MEDIA", d: "You allow permitted imagery to appear on our social channels only." },
  { t: "YES — WEBSITE & SOCIAL", d: "You allow permitted imagery to appear on both this website and social media." },
];

const POINTS = [
  { icon: Lock, t: "PRIVATE BY DEFAULT", d: "Customer submissions are treated as private unless permission is provided." },
  { icon: EyeOff, t: "NO UNAUTHORIZED PHOTOS", d: "Before/after images are never published without customer permission." },
  { icon: ShieldOff, t: "NO COLLECTION DISCLOSURE", d: "We do not publicly identify who owns a card or what is in a customer's collection." },
  { icon: Fingerprint, t: "NO CLOUT CHASING", d: "The value or rarity of a customer's collection does not override their privacy." },
];

export default function Confidentiality() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo
        title="Our Privacy Standard | CardScience HQ Confidentiality Policy"
        description="CardScience HQ is private by design. Customer identities, collections, values, submission details, photos and results are never used publicly without permission. Every submission is private by default."
        path="/confidentiality"
        jsonLd={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Confidentiality", path: "/confidentiality" },
        ])}
      />
      <PageHead eyebrow="Our Privacy Standard" title={<>YOUR CARDS.<br />YOUR BUSINESS.</>} tone="cyan" />

      <Wrap className="py-16">
        <Prose>
          <Impact tone="holo">Confidentiality is not an upgrade at CardScience HQ.</Impact>
          <Impact>It is part of our service.</Impact>
          <P className="pt-2">
            CardScience HQ will never intentionally disclose the identity of a client, the contents of a
            collection, the value of a collection, grading results, submission details or the use of our
            services without authorization from the client, except where disclosure is legally required.
          </P>
        </Prose>

        {/* Four trust pillars */}
        <div className="grid sm:grid-cols-2 gap-4 mt-12">
          {POINTS.map((p) => (
            <div key={p.t} className="rounded-2xl border border-[color:var(--line)] glass p-6">
              <div className="h-10 w-10 rounded-lg border border-[color:var(--line-2)] flex items-center justify-center text-[color:var(--cyan)] mb-4">
                <p.icon size={18} />
              </div>
              <h2 className="font-display font-bold text-white text-sm tracking-wide">{p.t}</h2>
              <p className="text-xs text-[color:var(--chrome-2)] leading-relaxed mt-2">{p.d}</p>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <p className="eyebrow mb-4">What that means</p>
          <DontList items={[
            "We do not require customers to publicly acknowledge that CardScience HQ worked on their collectibles.",
            "We do not require customers to tag us.",
            "We do not require customers to reveal that a collectible was submitted to CardScience HQ.",
            "We do not use high-profile cards, comics or collections for advertising without permission.",
            "We do not disclose customer collection values simply because the collection is impressive.",
          ]} />
        </div>

        {/* Permission control */}
        <div className="mt-14">
          <p className="eyebrow mb-3">You Control Publication</p>
          <h2 className="h-display text-2xl sm:text-3xl text-chrome">EVERY SUBMISSION CARRIES A PERMISSION SETTING.</h2>
          <p className="text-[color:var(--chrome-2)] leading-relaxed mt-4 max-w-2xl">
            When you submit a collectible, you choose exactly how (or whether) it may be shown. The setting
            defaults to private, and we never infer consent — a card marked private is never used in galleries,
            case studies, testimonials, social posts or promotional material.
          </p>
          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {PERMISSION_OPTIONS.map((o) => (
              <div key={o.t} className={`rounded-2xl p-5 ${o.def ? "holo-border" : "border border-[color:var(--line)] glass"}`}>
                <div className="flex items-center gap-2">
                  {o.def && <Lock size={14} className="text-[color:var(--cyan)]" />}
                  <p className={`font-display font-bold text-sm tracking-wide ${o.def ? "text-holo" : "text-white"}`}>{o.t}</p>
                  {o.def && <span className="ml-auto text-[10px] font-tech tracking-widest text-[color:var(--cyan)]">DEFAULT</span>}
                </div>
                <p className="text-xs text-[color:var(--chrome-2)] leading-relaxed mt-2">{o.d}</p>
              </div>
            ))}
          </div>
        </div>

        <Prose className="mt-12">
          <P>A collector may choose to publicly share their experience with us.</P>
          <P className="text-white font-semibold">That is their choice. Not ours.</P>
        </Prose>

        <div className="mt-12 rounded-2xl holo-border holo-glow p-8 text-center">
          <ShieldCheck size={30} className="text-holo mx-auto mb-3" />
          <p className="h-display text-2xl sm:text-3xl text-holo">100% CLIENT CONFIDENTIALITY.</p>
          <p className="text-sm text-[color:var(--chrome-2)] mt-3 max-w-lg mx-auto">
            If CardScience HQ shows you a customer&apos;s card, we had permission to show it.
          </p>
        </div>

        <div className="text-center">
          <Link to="/submit" className="btn-holo inline-flex items-center gap-2 rounded-xl px-8 py-4 text-sm mt-12">
            SUBMIT A COLLECTIBLE <ArrowRight size={16} />
          </Link>
        </div>
      </Wrap>
    </div>
  );
}
